import { createContext, use, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { TourProvider, useTour, type ITourStep } from '@kognitos/lattice';
import { markTourCompleted, markTourSkipped } from '../persistence';
import { useTourAnalytics } from '../hooks/use-tour-analytics';
import { useTourStore, selectClearActiveTour } from '../tours.store';
import { DEFAULT_SPOTLIGHT_PADDING } from '../constants';
import { ONBOARDING_CHAPTERS, getChapterStepInfo } from '../onboarding-flow';
import type {
  ETourId,
  IManagedTourProviderProps,
  ITourStepConfig,
} from '../types';

/**
 * Look up the chapter i18n title key for a tour, or undefined if the tour
 * is not part of the onboarding flow.
 */
function getChapterTitleKeyForTour(tourId: ETourId): string | undefined {
  return ONBOARDING_CHAPTERS.find(chapter => chapter.tours.includes(tourId))
    ?.titleKey;
}

// eslint-disable-next-line no-restricted-syntax -- reason: Internal context for provider boundary check, not application state
const ManagedTourContext = createContext(false);

/**
 * Returns true if the calling component is inside a ManagedTourProvider.
 */
export function useIsInsideManagedTourProvider(): boolean {
  return use(ManagedTourContext);
}

/**
 * Resolves i18n keys in step configs into display strings.
 *
 * @param steps - Step configs with translation keys
 * @param t - Translation function from useTranslation
 * @returns Resolved steps ready for lattice TourProvider
 */
function resolveSteps(
  steps: ITourStepConfig[],
  t: (key: string) => string,
  chapterName?: string,
): ITourStep[] {
  return steps.map((step, index, arr) => {
    const isLastLocalStep = index === arr.length - 1;
    const finishLabel = step.finishLabelKey
      ? t(step.finishLabelKey)
      : undefined;
    const nextLabel = step.nextLabelKey ? t(step.nextLabelKey) : undefined;
    // Promote finishLabel into nextLabel on the tour's last local step so
    // lattice's unconditional nextLabel branch renders it regardless.
    return {
      target: step.target,
      fixedPosition: step.fixedPosition,
      title: t(step.titleKey),
      description: step.descriptionKey ? t(step.descriptionKey) : undefined,
      position: step.position,
      image: step.image,
      icon: step.icon,
      chapterName,
      previousLabel: step.previousLabelKey
        ? t(step.previousLabelKey)
        : undefined,
      nextLabel: isLastLocalStep ? (nextLabel ?? finishLabel) : nextLabel,
      finishLabel,
    };
  });
}

/**
 * Filters tour steps to only include the specified step IDs.
 * Preserves the order defined in stepIds.
 *
 * @param steps - All step configs for the tour
 * @param stepIds - Optional subset of step IDs to include
 * @returns Filtered steps or all steps if stepIds is not provided
 */
function filterSteps(
  steps: ITourStepConfig[],
  stepIds?: string[],
): ITourStepConfig[] {
  if (!stepIds || stepIds.length === 0) {
    return steps;
  }

  return stepIds
    .map(id => steps.find(step => step.id === id))
    .filter((step): step is ITourStepConfig => step !== undefined);
}

/**
 * Filters tour steps by conditions: include a step only when it has no
 * condition or its condition value is true.
 *
 * @param steps - Step configs to filter
 * @param conditions - Map of step ID to boolean; steps not in the map are included
 * @returns Steps that pass their condition
 */
function filterStepsByConditions(
  steps: ITourStepConfig[],
  conditions?: Record<string, boolean>,
): ITourStepConfig[] {
  if (!conditions || Object.keys(conditions).length === 0) {
    return steps;
  }

  return steps.filter(
    step =>
      !step.id || !(step.id in conditions) || conditions[step.id] === true,
  );
}

/**
 * Internal component that tracks step changes and fires step_viewed analytics.
 * Rendered inside TourProvider to access useTour context.
 * Analytics run in useEffect so they fire only after commit, avoiding duplicate
 * events from concurrent render or Strict Mode double-mount.
 */
function TourStepTracker({
  tourId,
  analyticsConfig,
  steps,
  currentStepRef,
}: {
  tourId: string;
  analyticsConfig: IManagedTourProviderProps['tourConfig']['analytics'];
  steps: ITourStepConfig[];
  currentStepRef: React.RefObject<number>;
}) {
  const { currentStep, isActive } = useTour();
  const { trackStepViewed } = useTourAnalytics(tourId, analyticsConfig, steps);
  const prevTrackedStepRef = useRef<number>(-1);

  currentStepRef.current = currentStep;

  // eslint-disable-next-line no-restricted-syntax -- reason: Analytics side effect; defer to effect so we log once per step change and avoid duplicate events from concurrent render or Strict Mode
  useEffect(() => {
    if (isActive && currentStep !== prevTrackedStepRef.current) {
      prevTrackedStepRef.current = currentStep;
      trackStepViewed(currentStep);
    }
    if (!isActive) {
      prevTrackedStepRef.current = -1;
    }
  }, [isActive, currentStep, trackStepViewed]);

  return null;
}

/**
 * Enhanced TourProvider that automatically handles i18n resolution,
 * analytics tracking, persistence, and centralized state management.
 *
 * Wraps lattice's TourProvider and wires up:
 * - i18n key resolution for step titles and descriptions
 * - Statsig analytics event logging on complete/skip/step changes
 * - localStorage persistence for completion state
 * - Zustand store updates for active tour tracking
 * - Optional step filtering for multi-page tours
 *
 * @param props - Provider props including tour configuration
 * @returns Provider component wrapping children with tour capabilities
 */
export function ManagedTourProvider({
  children,
  tourConfig,
  stepIds,
  stepConditions,
  container,
  showOverlay,
  persistOnComplete = true,
  onComplete,
  onSkip,
}: IManagedTourProviderProps) {
  const { t } = useTranslation(tourConfig.namespace ?? 'tours');
  const activeSteps = filterStepsByConditions(
    filterSteps(tourConfig.steps, stepIds),
    stepConditions,
  );
  const chapterTitleKey = getChapterTitleKeyForTour(tourConfig.id);
  const chapterName = chapterTitleKey ? t(chapterTitleKey) : undefined;
  const resolvedSteps = resolveSteps(activeSteps, t, chapterName);
  const chapterStepInfo = getChapterStepInfo(tourConfig.id);

  const currentStepRef = useRef(0);
  const { trackCompleted, trackSkipped } = useTourAnalytics(
    tourConfig.id,
    tourConfig.analytics,
    activeSteps,
  );
  const clearActiveTour = useTourStore(selectClearActiveTour);

  // Clear active tour if this provider owned it and unmounts before completion
  // eslint-disable-next-line no-restricted-syntax -- reason: Cleanup stale activeTourId on unmount to unblock subsequent tours
  useEffect(() => {
    return () => {
      if (useTourStore.getState().activeTourId === tourConfig.id) {
        clearActiveTour();
      }
    };
  }, [tourConfig.id, clearActiveTour]);

  const handleComplete = useCallback(() => {
    if (persistOnComplete) {
      markTourCompleted(tourConfig.id, tourConfig.persistence);
    }
    trackCompleted();
    clearActiveTour();
    onComplete?.();
  }, [
    persistOnComplete,
    tourConfig.id,
    tourConfig.persistence,
    trackCompleted,
    clearActiveTour,
    onComplete,
  ]);

  const handleSkip = useCallback(() => {
    markTourSkipped(tourConfig.id, tourConfig.persistence);
    trackSkipped(currentStepRef.current);
    clearActiveTour();
    onSkip?.();
  }, [
    tourConfig.id,
    tourConfig.persistence,
    trackSkipped,
    clearActiveTour,
    onSkip,
  ]);

  return (
    <ManagedTourContext value>
      <TourProvider
        steps={resolvedSteps}
        variant={tourConfig.variant ?? 'default'}
        size={tourConfig.size ?? 'sm'}
        onComplete={handleComplete}
        onSkip={handleSkip}
        alwaysShowPrevious={tourConfig.alwaysShowPrevious}
        spotlightPadding={
          tourConfig.spotlightPadding ?? DEFAULT_SPOTLIGHT_PADDING
        }
        spotlightRadius={tourConfig.spotlightRadius}
        container={container}
        showOverlay={showOverlay}
        stepOffset={chapterStepInfo?.offset}
        totalChapterSteps={chapterStepInfo?.total}
      >
        <TourStepTracker
          tourId={tourConfig.id}
          analyticsConfig={tourConfig.analytics}
          steps={activeSteps}
          currentStepRef={currentStepRef}
        />
        {children}
      </TourProvider>
    </ManagedTourContext>
  );
}
